import { supabase } from '@/integrations/supabase/client';

export interface ExpenseCategory {
  id: string;
  user_id?: string;
  name: string;
  description?: string;
  category_type?: 'cogs' | 'operating' | 'administrative' | 'other' | 'equipment';
  is_active?: boolean;
  created_at: string;
  updated_at: string;
}

export interface Expense {
  id: string;
  user_id: string;
  category_id: string;
  title: string;
  description?: string;
  amount: number;
  currency: string;
  expense_date: string;
  payment_method?: string;
  receipt_url?: string;
  is_recurring: boolean;
  recurring_frequency?: 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly';
  next_due_date?: string;
  status: 'paid' | 'pending' | 'cancelled';
  created_at: string;
  updated_at: string;
  category?: ExpenseCategory;
}

export interface FinancialAccount {
  id: string;
  user_id: string;
  account_name: string;
  account_type: 'asset' | 'liability' | 'equity';
  current_balance: number;
  currency?: string;
  is_active?: boolean;
  created_at: string;
  updated_at: string;
}

export interface TaxSettings {
  id: string;
  restaurant_id: string;
  tax_year: number;
  business_type?: string;
  tax_id_number?: string;
  fiscal_year_start_month: number;
  default_tax_rate: number;
  service_charge_taxable: boolean;
  delivery_fee_taxable: boolean;
  accounting_method: 'cash' | 'accrual';
  created_at: string;
  updated_at: string;
}

export interface InventoryCount {
  id: string;
  user_id: string;
  period_start_date: string;
  period_end_date: string;
  beginning_inventory: number;
  ending_inventory: number;
  notes?: string;
  created_at: string;
}

export interface WasteTracking {
  id: string;
  user_id: string;
  waste_date: string;
  category: 'spoilage' | 'preparation' | 'customer_return' | 'other';
  item_name: string;
  quantity: number;
  cost_amount: number;
  reason?: string;
  created_at: string;
}

export interface ProfitLossReport {
  period: {
    start_date: string;
    end_date: string;
  };
  cogs_calculation_method?: 'inventory' | 'recipe' | 'purchases' | 'none';
  data_sources?: {
    beginning_inventory_date: string | null;
    ending_inventory_date: string | null;
    has_beginning_inventory: boolean;
    waste_records_count: number;
  };
  revenue: {
    total_revenue: number;
    dine_in_revenue: number;
    pickup_revenue: number;
    delivery_revenue: number;
    total_tax: number;
    total_service_charge: number;
    total_discounts: number;
    delivery_fees: number;
    total_orders: number;
    net_revenue: number;
    average_order_value: number;
    average_order_value_by_type: {
      dine_in: number;
      pickup: number;
      delivery: number;
    };
  };
  cost_of_goods_sold: {
    beginning_inventory: number;
    purchases: number;
    ending_inventory: number;
    food_cost: number;
    waste_amount: number;
    total_cogs: number;
    cogs_percentage: number;
    waste_percentage: number;
  };
  gross_profit: {
    amount: number;
    margin_percentage: number;
  };
  operating_expenses: {
    payroll: number;
    operating_expenses: number;
    administrative_expenses: number;
    other_expenses: number;
    total_operating_expenses: number;
    expense_breakdown: Array<{
      category: string;
      amount: number;
      percentage: number;
    }>;
  };
  labor_metrics: {
    total_payroll: number;
    labor_cost_percentage: number;
    cost_per_order: number;
  };
  net_profit: {
    amount: number;
    margin_percentage: number;
  };
  prime_cost: {
    amount: number;
    percentage: number;
  };
  generated_at: string;
}

export interface CashFlowReport {
  period: {
    start_date: string;
    end_date: string;
  };
  operating_activities: {
    cash_from_sales: number;
    cash_to_suppliers: number;
    cash_to_employees: number;
    cash_for_operating_expenses: number;
    net_operating_cash_flow: number;
  };
  investing_activities: {
    equipment_purchases: number;
    asset_sales: number;
    net_investing_cash_flow: number;
  };
  financing_activities: {
    owner_contributions: number;
    loan_proceeds: number;
    loan_payments: number;
    owner_withdrawals: number;
    net_financing_cash_flow: number;
  };
  net_cash_flow: number;
  beginning_cash_balance: number;
  ending_cash_balance: number;
  generated_at: string;
}

export interface ExpenseSummary {
  category: string;
  category_type: string;
  total_amount: number;
  expense_count: number;
  percentage_of_total: number;
  average_expense: number;
}

class FinancialServiceError extends Error {
  constructor(message: string, public cause?: unknown) {
    super(message);
    this.name = 'FinancialServiceError';
  }
}

// Expense Categories
export async function fetchExpenseCategories(): Promise<ExpenseCategory[]> {
  try {
    // Get authenticated user from session
    const { data: { session } } = await supabase.auth.getSession();
    const user = session?.user;
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('expense_categories')
      .select('*')
      .eq('user_id', user.id);

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.warn('Failed to fetch expense categories:', error);
    return [];
  }
}

export async function createExpenseCategory(category: Omit<ExpenseCategory, 'id' | 'created_at' | 'updated_at'>): Promise<ExpenseCategory> {
  try {
    const { data, error } = await supabase
      .from('expense_categories')
      .insert(category)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    throw new FinancialServiceError('Failed to create expense category', error);
  }
}

export async function updateExpenseCategory(id: string, updates: Partial<ExpenseCategory>): Promise<ExpenseCategory> {
  try {
    const { data, error } = await supabase
      .from('expense_categories')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    throw new FinancialServiceError('Failed to update expense category', error);
  }
}

// Expenses
export async function fetchExpenses(filters?: {
  branchId?: string;
  categoryId?: string;
  startDate?: string;
  endDate?: string;
  status?: string;
  limit?: number;
}): Promise<Expense[]> {
  try {
    // Get authenticated user from session
    const { data: { session } } = await supabase.auth.getSession();
    const user = session?.user;
    if (!user) throw new Error('User not authenticated');

    let query = supabase
      .from('expenses')
      .select(`
        *,
        category:expense_categories(*)
      `)
      .eq('user_id', user.id)
      .order('expense_date', { ascending: false });

    if (filters?.categoryId) {
      query = query.eq('category_id', filters.categoryId);
    }
    if (filters?.startDate) {
      query = query.gte('expense_date', filters.startDate);
    }
    if (filters?.endDate) {
      query = query.lte('expense_date', filters.endDate);
    }
    if (filters?.status) {
      query = query.eq('status', filters.status);
    }
    if (filters?.limit) {
      query = query.limit(filters.limit);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.warn('Failed to fetch expenses:', error);
    return [];
  }
}

export async function createExpense(expense: Omit<Expense, 'id' | 'created_at' | 'updated_at'>): Promise<Expense> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const user = session?.user;
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('expenses')
      .insert({ ...expense, user_id: user.id })
      .select(`
        *,
        category:expense_categories(*)
      `)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.warn('Failed to create expense:', error);
    throw error;
  }
}

export async function updateExpense(id: string, updates: Partial<Expense>): Promise<Expense> {
  try {
    const { data, error } = await supabase
      .from('expenses')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id)
      .select(`
        *,
        category:expense_categories(*)
      `)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    console.warn('Failed to update expense:', error);
    throw error;
  }
}

export async function deleteExpense(id: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('expenses')
      .delete()
      .eq('id', id);

    if (error) throw error;
  } catch (error) {
    console.warn('Failed to delete expense:', error);
    throw error;
  }
}

// Financial Accounts
export async function fetchFinancialAccounts(): Promise<FinancialAccount[]> {
  try {
    // Get authenticated user from session
    const { data: { session } } = await supabase.auth.getSession();
    const user = session?.user;
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('financial_accounts')
      .select('*')
      .eq('user_id', user.id)
      .eq('is_active', true)
      .order('account_type, account_name');

    if (error) {
      console.warn('Financial Accounts table not found or error:', error.message);
      return [];
    }
    return data || [];
  } catch (error) {
    console.warn('Failed to fetch financial accounts:', error);
    return [];
  }
}

// Tax Settings
export async function fetchTaxSettings(restaurantId: string, taxYear?: number): Promise<TaxSettings | null> {
  try {
    const year = taxYear || new Date().getFullYear();
    const { data, error } = await supabase
      .from('tax_settings')
      .select('*')
      .eq('restaurant_id', restaurantId)
      .eq('tax_year', year)
      .single();

    if (error && error.code !== 'PGRST116') throw error;
    return data;
  } catch (error) {
    throw new FinancialServiceError('Failed to fetch tax settings', error);
  }
}

export async function upsertTaxSettings(settings: Omit<TaxSettings, 'id' | 'created_at' | 'updated_at'>): Promise<TaxSettings> {
  try {
    const { data, error } = await supabase
      .from('tax_settings')
      .upsert(
        { ...settings, updated_at: new Date().toISOString() },
        { onConflict: 'restaurant_id,tax_year' }
      )
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    throw new FinancialServiceError('Failed to save tax settings', error);
  }
}

// Reports
export async function generateProfitLossReport(
  startDate: string,
  endDate: string,
  branchId?: string
): Promise<ProfitLossReport> {
  try {
    // Get authenticated user from session
    const { data: { session } } = await supabase.auth.getSession();
    const user = session?.user;
    if (!user) throw new Error('User not authenticated');

    // Get revenue from completed orders
    let orderQuery = supabase
      .from('orders')
      .select('id, total_amount, service_amount, tax_amount, created_at, branch_id, order_type')
      .eq('status', 'completed');

    if (branchId) {
      orderQuery = orderQuery.eq('branch_id', branchId);
    }

    const { data: allOrders, error: orderError } = await orderQuery;

    if (orderError) throw orderError;

    // Filter orders by date range
    const orders = (allOrders || []).filter(order => {
      const orderDate = new Date(order.created_at);
      const start = new Date(startDate);
      const end = new Date(endDate);
      
      return orderDate >= start && orderDate <= end;
    });

    // Calculate revenue by type
    const dineInRevenue = orders
      .filter(o => o.order_type === 'dine_in')
      .reduce((sum, order) => sum + (order.total_amount || 0), 0);
    
    const pickupRevenue = orders
      .filter(o => o.order_type === 'pickup')
      .reduce((sum, order) => sum + (order.total_amount || 0), 0);
    
    const deliveryRevenue = orders
      .filter(o => o.order_type === 'delivery')
      .reduce((sum, order) => sum + (order.total_amount || 0), 0);

    const totalRevenue = orders.reduce((sum, order) => sum + (order.total_amount || 0), 0);
    const totalServiceFees = orders.reduce((sum, order) => sum + (order.service_amount || 0), 0);
    const totalTaxes = orders.reduce((sum, order) => sum + (order.tax_amount || 0), 0);
    const totalOrders = orders.length;
    const netRevenue = totalRevenue - totalTaxes;

    // Get expenses for authenticated user
    let expenseQuery = supabase
      .from('expenses')
      .select('amount, category:expense_categories(category_type)')
      .eq('user_id', user.id)
      .eq('status', 'paid')
      .gte('expense_date', startDate)
      .lte('expense_date', endDate);

    const { data: expenses, error: expenseError } = await expenseQuery;

    if (expenseError) throw expenseError;

    // Calculate expenses by category
    const operatingExpenses = (expenses || [])
      .filter(e => e.category?.category_type === 'operating')
      .reduce((sum, e) => sum + (e.amount || 0), 0);

    const administrativeExpenses = (expenses || [])
      .filter(e => e.category?.category_type === 'administrative')
      .reduce((sum, e) => sum + (e.amount || 0), 0);

    const otherExpenses = (expenses || [])
      .filter(e => e.category?.category_type === 'other')
      .reduce((sum, e) => sum + (e.amount || 0), 0);

    // Get COGS using proper inventory method from existing inventory system
    
    // Method 1: Try to get from order_cogs (recipe-based)
    // Query order_cogs directly for the order IDs we have
    const orderIds = orders.map(o => o.id);
    
    let orderCogsQuery = supabase
      .from('order_cogs')
      .select('total_cogs, order_id')
      .in('order_id', orderIds);

    const { data: orderCogs } = await orderCogsQuery;

    const recipeCOGS = (orderCogs || []).reduce((sum, oc) => sum + (oc.total_cogs || 0), 0);

    // Method 2: Calculate inventory values from physical counts
    // Get beginning inventory - most recent count BEFORE period start
    let physicalCountBeginQuery = supabase
      .from('physical_inventory_counts')
      .select('actual_stock, material_id, raw_materials!inner(cost_per_unit), counted_at')
      .eq('user_id', user.id)
      .lte('counted_at', startDate)
      .order('counted_at', { ascending: false })
      .limit(1000); // Get all materials from last count

    if (branchId) {
      physicalCountBeginQuery = physicalCountBeginQuery.eq('branch_id', branchId);
    }

    const { data: beginPhysicalCount } = await physicalCountBeginQuery;

    let beginningInventory = 0;
    let beginningInventoryDate: string | null = null;
    
    if (beginPhysicalCount && beginPhysicalCount.length > 0) {
      // Calculate beginning inventory from last physical count
      beginningInventory = beginPhysicalCount.reduce((sum, count: any) => {
        const costPerUnit = count.raw_materials?.cost_per_unit || 0;
        return sum + (count.actual_stock || 0) * costPerUnit;
      }, 0);
      beginningInventoryDate = beginPhysicalCount[0].counted_at;
    }

    // Get ending inventory - most recent count AFTER or AT period end
    // This handles historical reporting correctly
    let physicalCountEndQuery = supabase
      .from('physical_inventory_counts')
      .select('actual_stock, material_id, raw_materials!inner(cost_per_unit), counted_at')
      .eq('user_id', user.id)
      .gte('counted_at', endDate)
      .order('counted_at', { ascending: true })
      .limit(1000);

    if (branchId) {
      physicalCountEndQuery = physicalCountEndQuery.eq('branch_id', branchId);
    }

    const { data: endPhysicalCount } = await physicalCountEndQuery;

    let endingInventory = 0;
    let endingInventoryDate: string | null = null;
    
    if (endPhysicalCount && endPhysicalCount.length > 0) {
      // Calculate ending inventory from next physical count after period
      endingInventory = endPhysicalCount.reduce((sum, count: any) => {
        const costPerUnit = count.raw_materials?.cost_per_unit || 0;
        return sum + (count.actual_stock || 0) * costPerUnit;
      }, 0);
      endingInventoryDate = endPhysicalCount[0].counted_at;
    } else {
      // No physical count after period - use current raw materials
      let rawMaterialsQuery = supabase
        .from('raw_materials')
        .select('current_stock, cost_per_unit')
        .eq('user_id', user.id);
      
      if (branchId) {
        rawMaterialsQuery = rawMaterialsQuery.eq('branch_id', branchId);
      }

      const { data: rawMaterials } = await rawMaterialsQuery;
      endingInventory = (rawMaterials || []).reduce(
        (sum, m) => sum + (m.current_stock || 0) * (m.cost_per_unit || 0), 
        0
      );
      endingInventoryDate = new Date().toISOString();
    }

    // Get COGS purchases (inventory purchases from expenses)
    const cogsPurchases = (expenses || [])
      .filter(e => e.category?.category_type === 'cogs')
      .reduce((sum, e) => sum + (e.amount || 0), 0);

    // Calculate waste from physical count discrepancies in the period
    let physicalCountWasteQuery = supabase
      .from('physical_inventory_counts')
      .select('waste_amount, material_id, raw_materials!inner(cost_per_unit)')
      .eq('user_id', user.id)
      .gte('counted_at', startDate)
      .lte('counted_at', endDate);

    if (branchId) {
      physicalCountWasteQuery = physicalCountWasteQuery.eq('branch_id', branchId);
    }

    const { data: physicalCountWaste } = await physicalCountWasteQuery;

    const totalWaste = (physicalCountWaste || []).reduce((sum, w: any) => {
      const costPerUnit = w.raw_materials?.cost_per_unit || 0;
      return sum + (w.waste_amount || 0) * costPerUnit;
    }, 0);

    // Calculate COGS using best available method
    let totalCOGS: number;
    let foodCost = 0;
    let cogsCalculationMethod = 'purchases'; // Default fallback

    // Decision logic for COGS calculation method with quality checks
    const hasBeginningInventory = beginningInventory > 0;
    const hasActualEndingCount = endPhysicalCount && endPhysicalCount.length > 0;
    
    // Check if inventory data is actually reliable
    // 1. Must have both beginning and ending inventory
    // 2. Ending must be from actual physical count (not current stock)
    // 3. Values should be different (otherwise no change = suspicious)
    // 4. If using current stock as ending, require reasonable purchases
    const isInventoryDataReliable = 
      hasBeginningInventory && 
      hasActualEndingCount && 
      beginningInventory !== endingInventory;
    
    // If no actual ending count, only trust inventory method if purchases are significant
    const hasFallbackEndingWithPurchases = 
      hasBeginningInventory && 
      !hasActualEndingCount && 
      cogsPurchases > beginningInventory * 0.1; // Purchases should be at least 10% of beginning
    
    if (isInventoryDataReliable || hasFallbackEndingWithPurchases) {
      // Method 1: Inventory accounting (most accurate when we have real counts)
      foodCost = beginningInventory + cogsPurchases - endingInventory;
      
      // Sanity check: If result is negative with low purchases, something is wrong
      if (foodCost < 0 && cogsPurchases < beginningInventory * 0.5) {
        // Data looks suspicious - fall through to recipe method instead
        if (recipeCOGS > 0) {
          totalCOGS = recipeCOGS + totalWaste;
          foodCost = recipeCOGS;
          cogsCalculationMethod = 'recipe';
        } else {
          totalCOGS = cogsPurchases + totalWaste;
          foodCost = cogsPurchases;
          cogsCalculationMethod = 'purchases';
        }
      } else {
        totalCOGS = foodCost + totalWaste;
        cogsCalculationMethod = 'inventory';
      }
    } else if (recipeCOGS > 0) {
      // Method 2: Recipe-based (best for first month or when no reliable counts)
      totalCOGS = recipeCOGS + totalWaste;
      foodCost = recipeCOGS;
      cogsCalculationMethod = 'recipe';
    } else if (cogsPurchases > 0) {
      // Method 3: Purchases only (fallback, least accurate)
      totalCOGS = cogsPurchases + totalWaste;
      foodCost = cogsPurchases;
      cogsCalculationMethod = 'purchases';
    } else {
      // No data at all
      totalCOGS = totalWaste;
      foodCost = 0;
      cogsCalculationMethod = 'none';
    }

    // Get payroll expenses
    const payrollExpenses = (expenses || [])
      .filter(e => e.category?.category_type === 'operating' && 
                   (e.category?.name?.toLowerCase().includes('payroll') ||
                    e.category?.name?.toLowerCase().includes('salary') ||
                    e.category?.name?.toLowerCase().includes('wage')))
      .reduce((sum, e) => sum + (e.amount || 0), 0);

    const otherOperatingExpenses = operatingExpenses - payrollExpenses;

    const totalOperatingExpenses = operatingExpenses + administrativeExpenses + otherExpenses;
    const totalExpenses = totalCOGS + totalOperatingExpenses;

    // Calculate detailed expense breakdown
    const expenseBreakdown = [
      { category: 'Payroll', amount: payrollExpenses, percentage: totalRevenue > 0 ? (payrollExpenses / totalRevenue) * 100 : 0 },
      { category: 'Other Operating', amount: otherOperatingExpenses, percentage: totalRevenue > 0 ? (otherOperatingExpenses / totalRevenue) * 100 : 0 },
      { category: 'Administrative', amount: administrativeExpenses, percentage: totalRevenue > 0 ? (administrativeExpenses / totalRevenue) * 100 : 0 },
      { category: 'Other', amount: otherExpenses, percentage: totalRevenue > 0 ? (otherExpenses / totalRevenue) * 100 : 0 }
    ].filter(e => e.amount > 0);

    // Calculate profit
    const grossProfit = totalRevenue - totalCOGS;
    const grossProfitMargin = totalRevenue > 0 ? (grossProfit / totalRevenue) * 100 : 0;
    
    const netProfit = totalRevenue - totalExpenses;
    const netProfitMargin = totalRevenue > 0 ? (netProfit / totalRevenue) * 100 : 0;

    const cogsPercentage = totalRevenue > 0 ? (totalCOGS / totalRevenue) * 100 : 0;
    const wastePercentage = (foodCost + totalWaste) > 0 ? (totalWaste / (foodCost + totalWaste)) * 100 : 0;

    // Calculate AOV metrics
    const avgOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;
    const dineInOrders = orders.filter(o => o.order_type === 'dine_in');
    const pickupOrders = orders.filter(o => o.order_type === 'pickup');
    const deliveryOrders = orders.filter(o => o.order_type === 'delivery');

    // Prime Cost (COGS + Labor)
    const primeCost = totalCOGS + payrollExpenses;
    const primeCostPercentage = totalRevenue > 0 ? (primeCost / totalRevenue) * 100 : 0;

    return {
      period: {
        start_date: startDate,
        end_date: endDate
      },
      cogs_calculation_method: cogsCalculationMethod,
      data_sources: {
        beginning_inventory_date: beginningInventoryDate,
        ending_inventory_date: endingInventoryDate,
        has_beginning_inventory: hasBeginningInventory,
        waste_records_count: (physicalCountWaste || []).length
      },
      revenue: {
        total_revenue: totalRevenue,
        dine_in_revenue: dineInRevenue,
        pickup_revenue: pickupRevenue,
        delivery_revenue: deliveryRevenue,
        total_tax: totalTaxes,
        total_service_charge: totalServiceFees,
        total_discounts: 0, // TODO: Add discounts tracking
        delivery_fees: 0, // TODO: Extract from service charges
        total_orders: totalOrders,
        net_revenue: netRevenue,
        average_order_value: avgOrderValue,
        average_order_value_by_type: {
          dine_in: dineInOrders.length > 0 ? dineInRevenue / dineInOrders.length : 0,
          pickup: pickupOrders.length > 0 ? pickupRevenue / pickupOrders.length : 0,
          delivery: deliveryOrders.length > 0 ? deliveryRevenue / deliveryOrders.length : 0
        }
      },
      cost_of_goods_sold: {
        beginning_inventory: beginningInventory,
        purchases: cogsPurchases,
        ending_inventory: endingInventory,
        food_cost: foodCost,
        waste_amount: totalWaste,
        total_cogs: totalCOGS,
        cogs_percentage: cogsPercentage,
        waste_percentage: wastePercentage
      },
      gross_profit: {
        amount: grossProfit,
        margin_percentage: grossProfitMargin
      },
      operating_expenses: {
        payroll: payrollExpenses,
        operating_expenses: otherOperatingExpenses,
        administrative_expenses: administrativeExpenses,
        other_expenses: otherExpenses,
        total_operating_expenses: totalOperatingExpenses,
        expense_breakdown: expenseBreakdown
      },
      labor_metrics: {
        total_payroll: payrollExpenses,
        labor_cost_percentage: totalRevenue > 0 ? (payrollExpenses / totalRevenue) * 100 : 0,
        cost_per_order: totalOrders > 0 ? payrollExpenses / totalOrders : 0
      },
      net_profit: {
        amount: netProfit,
        margin_percentage: netProfitMargin
      },
      prime_cost: {
        amount: primeCost,
        percentage: primeCostPercentage
      },
      generated_at: new Date().toISOString()
    };
  } catch (error) {
    throw new FinancialServiceError('Failed to generate profit & loss report', error);
  }
}

export async function generateCashFlowReport(
  startDate: string,
  endDate: string,
  branchId?: string
): Promise<CashFlowReport> {
  try {
    // Get authenticated user from session
    const { data: { session } } = await supabase.auth.getSession();
    const user = session?.user;
    if (!user) throw new Error('User not authenticated');

    // Get cash transactions from orders
    let orderQuery = supabase
      .from('orders')
      .select('total_amount, payment_method, created_at')
      .eq('status', 'completed')
      .gte('created_at', startDate)
      .lte('created_at', endDate);

    if (branchId) {
      orderQuery = orderQuery.eq('branch_id', branchId);
    }

    const { data: orderData, error: orderError } = await orderQuery;

    if (orderError) throw orderError;

    // Get cash expenses for authenticated user
    let expenseQuery = supabase
      .from('expenses')
      .select(`
        amount,
        expense_date,
        category:expense_categories(category_type)
      `)
      .eq('user_id', user.id)
      .gte('expense_date', startDate)
      .lte('expense_date', endDate)
      .eq('status', 'paid');

    const { data: expenseData, error: expenseError } = await expenseQuery;

    if (expenseError) throw expenseError;

    // Calculate cash flows
    const cashFromSales = orderData?.reduce((sum, order) => sum + (order.total_amount || 0), 0) || 0;
    
    const operatingExpenses = expenseData?.filter(exp => 
      exp.category?.category_type === 'operating'
    ).reduce((sum, exp) => sum + exp.amount, 0) || 0;

    const cogsExpenses = expenseData?.filter(exp => 
      exp.category?.category_type === 'cogs'
    ).reduce((sum, exp) => sum + exp.amount, 0) || 0;

    const adminExpenses = expenseData?.filter(exp => 
      exp.category?.category_type === 'administrative'
    ).reduce((sum, exp) => sum + exp.amount, 0) || 0;

    const equipmentExpenses = expenseData?.filter(exp => 
      exp.category?.category_type === 'equipment'
    ).reduce((sum, exp) => sum + exp.amount, 0) || 0;

    const netOperatingCashFlow = cashFromSales - cogsExpenses - operatingExpenses;
    const netInvestingCashFlow = -equipmentExpenses;
    const netFinancingCashFlow = 0; // Would need additional data for loans, etc.
    const netCashFlow = netOperatingCashFlow + netInvestingCashFlow + netFinancingCashFlow;

    return {
      period: {
        start_date: startDate,
        end_date: endDate
      },
      operating_activities: {
        cash_from_sales: cashFromSales,
        cash_to_suppliers: cogsExpenses,
        cash_to_employees: 0, // Would need payroll data
        cash_for_operating_expenses: operatingExpenses + adminExpenses,
        net_operating_cash_flow: netOperatingCashFlow
      },
      investing_activities: {
        equipment_purchases: equipmentExpenses,
        asset_sales: 0,
        net_investing_cash_flow: netInvestingCashFlow
      },
      financing_activities: {
        owner_contributions: 0,
        loan_proceeds: 0,
        loan_payments: 0,
        owner_withdrawals: 0,
        net_financing_cash_flow: netFinancingCashFlow
      },
      net_cash_flow: netCashFlow,
      beginning_cash_balance: 0, // Would need to calculate from previous period
      ending_cash_balance: netCashFlow,
      generated_at: new Date().toISOString()
    };
  } catch (error) {
    throw new FinancialServiceError('Failed to generate cash flow report', error);
  }
}

export async function generateExpenseSummary(
  startDate: string,
  endDate: string,
  branchId?: string
): Promise<ExpenseSummary[]> {
  try {
    // Get authenticated user from session
    const { data: { session } } = await supabase.auth.getSession();
    const user = session?.user;
    if (!user) throw new Error('User not authenticated');

    let query = supabase
      .from('expenses')
      .select(`
        amount,
        category:expense_categories(name, category_type)
      `)
      .eq('user_id', user.id)
      .gte('expense_date', startDate)
      .lte('expense_date', endDate)
      .eq('status', 'paid');

    const { data, error } = await query;

    if (error) throw error;

    const expenses = data || [];
    const totalAmount = expenses.reduce((sum, exp) => sum + exp.amount, 0);
    
    // Group by category
    const categoryGroups = expenses.reduce((groups, expense) => {
      const categoryName = expense.category?.name || 'Other';
      const categoryType = expense.category?.category_type || 'other';
      
      if (!groups[categoryName]) {
        groups[categoryName] = {
          category: categoryName,
          category_type: categoryType,
          total_amount: 0,
          expense_count: 0,
          percentage_of_total: 0,
          average_expense: 0
        };
      }
      
      groups[categoryName].total_amount += expense.amount;
      groups[categoryName].expense_count += 1;
      
      return groups;
    }, {} as Record<string, ExpenseSummary>);

    // Calculate percentages and averages
    const summaries = Object.values(categoryGroups).map(summary => ({
      ...summary,
      percentage_of_total: totalAmount > 0 ? (summary.total_amount / totalAmount) * 100 : 0,
      average_expense: summary.expense_count > 0 ? summary.total_amount / summary.expense_count : 0
    }));

    return summaries.sort((a, b) => b.total_amount - a.total_amount);
  } catch (error) {
    throw new FinancialServiceError('Failed to generate expense summary', error);
  }
}

// Inventory Management
export async function saveInventoryCount(inventoryData: {
  period_start_date: string;
  period_end_date: string;
  beginning_inventory: number;
  ending_inventory: number;
  notes?: string;
}): Promise<InventoryCount> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const user = session?.user;
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('inventory_counts')
      .upsert({
        user_id: user.id,
        ...inventoryData,
        created_by: user.id,
        updated_at: new Date().toISOString()
      }, {
        onConflict: 'user_id,period_end_date'
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    throw new FinancialServiceError('Failed to save inventory count', error);
  }
}

export async function fetchInventoryCounts(startDate?: string, endDate?: string): Promise<InventoryCount[]> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const user = session?.user;
    if (!user) throw new Error('User not authenticated');

    let query = supabase
      .from('inventory_counts')
      .select('*')
      .eq('user_id', user.id)
      .order('period_end_date', { ascending: false });

    if (startDate) {
      query = query.gte('period_end_date', startDate);
    }
    if (endDate) {
      query = query.lte('period_end_date', endDate);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.warn('Failed to fetch inventory counts:', error);
    return [];
  }
}

// Waste Tracking
export async function addWasteRecord(wasteData: {
  waste_date: string;
  category: 'spoilage' | 'preparation' | 'customer_return' | 'other';
  item_name: string;
  quantity: number;
  cost_amount: number;
  reason?: string;
}): Promise<WasteTracking> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const user = session?.user;
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('waste_tracking')
      .insert({
        user_id: user.id,
        ...wasteData,
        created_by: user.id
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    throw new FinancialServiceError('Failed to add waste record', error);
  }
}

export async function fetchWasteRecords(startDate?: string, endDate?: string): Promise<WasteTracking[]> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const user = session?.user;
    if (!user) throw new Error('User not authenticated');

    let query = supabase
      .from('waste_tracking')
      .select('*')
      .eq('user_id', user.id)
      .order('waste_date', { ascending: false });

    if (startDate) {
      query = query.gte('waste_date', startDate);
    }
    if (endDate) {
      query = query.lte('waste_date', endDate);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.warn('Failed to fetch waste records:', error);
    return [];
  }
}

// Tax Report
export async function generateTaxReport(
  taxYear: number
): Promise<{
  gross_revenue: number;
  taxable_revenue: number;
  tax_collected: number;
  deductible_expenses: number;
  net_taxable_income: number;
  estimated_tax_owed: number;
  quarterly_payments: number[];
  generated_at: string;
}> {
  try {
    // Get authenticated user from session
    const { data: { session } } = await supabase.auth.getSession();
    const user = session?.user;
    if (!user) throw new Error('User not authenticated');

    const startDate = `${taxYear}-01-01`;
    const endDate = `${taxYear}-12-31`;

    // Get revenue data
    let orderQuery = supabase
      .from('orders')
      .select('total_amount, tax_amount, service_amount, subtotal_amount, created_at')
      .gte('created_at', startDate)
      .lte('created_at', endDate)
      .eq('status', 'completed');

    const { data: orders, error: orderError } = await orderQuery;

    if (orderError) throw orderError;

    // Get deductible expenses for authenticated user
    const { data: expenses, error: expenseError } = await supabase
      .from('expenses')
      .select('amount, expense_date')
      .eq('user_id', user.id)
      .gte('expense_date', startDate)
      .lte('expense_date', endDate)
      .eq('status', 'paid');

    if (expenseError) throw expenseError;

    const grossRevenue = orders?.reduce((sum, order) => sum + (order.total_amount || 0), 0) || 0;
    const taxCollected = orders?.reduce((sum, order) => sum + (order.tax_amount || 0), 0) || 0;
    const taxableRevenue = grossRevenue - taxCollected;
    const deductibleExpenses = expenses?.reduce((sum, expense) => sum + expense.amount, 0) || 0;
    const netTaxableIncome = Math.max(0, taxableRevenue - deductibleExpenses);

    // Estimate tax owed (this would vary by jurisdiction)
    const estimatedTaxRate = 0.25; // 25% estimated tax rate
    const estimatedTaxOwed = netTaxableIncome * estimatedTaxRate;

    // Calculate quarterly payments (simplified)
    const quarterlyPayment = estimatedTaxOwed / 4;
    const quarterlyPayments = [quarterlyPayment, quarterlyPayment, quarterlyPayment, quarterlyPayment];

    return {
      gross_revenue: grossRevenue,
      taxable_revenue: taxableRevenue,
      tax_collected: taxCollected,
      deductible_expenses: deductibleExpenses,
      net_taxable_income: netTaxableIncome,
      estimated_tax_owed: estimatedTaxOwed,
      quarterly_payments: quarterlyPayments,
      generated_at: new Date().toISOString()
    };
  } catch (error) {
    throw new FinancialServiceError('Failed to generate tax report', error);
  }
}

// Export to Excel/CSV
export async function exportFinancialData(
  restaurantId: string,
  reportType: 'expenses' | 'profit_loss' | 'cash_flow' | 'tax_report',
  startDate: string,
  endDate: string,
  format: 'excel' | 'csv' = 'excel'
): Promise<Blob> {
  try {
    let data: any;
    let filename: string;

    switch (reportType) {
      case 'expenses':
        data = await fetchExpenses(restaurantId, { startDate, endDate });
        filename = `expenses_${startDate}_to_${endDate}`;
        break;
      case 'profit_loss':
        data = await generateProfitLossReport(restaurantId, startDate, endDate);
        filename = `profit_loss_${startDate}_to_${endDate}`;
        break;
      case 'cash_flow':
        data = await generateCashFlowReport(restaurantId, startDate, endDate);
        filename = `cash_flow_${startDate}_to_${endDate}`;
        break;
      case 'tax_report':
        const year = new Date(startDate).getFullYear();
        data = await generateTaxReport(restaurantId, year);
        filename = `tax_report_${year}`;
        break;
      default:
        throw new Error('Invalid report type');
    }

    // Convert data to CSV or Excel format
    if (format === 'csv') {
      const csv = convertToCSV(data, reportType);
      return new Blob([csv], { type: 'text/csv' });
    } else {
      // For Excel format, you would need a library like xlsx
      // This is a simplified implementation
      const csv = convertToCSV(data, reportType);
      return new Blob([csv], { type: 'application/vnd.ms-excel' });
    }
  } catch (error) {
    throw new FinancialServiceError('Failed to export financial data', error);
  }
}

function convertToCSV(data: any, reportType: string): string {
  switch (reportType) {
    case 'expenses':
      const expenses = data as Expense[];
      const expenseHeaders = ['Date', 'Title', 'Category', 'Amount', 'Payment Method', 'Status'];
      const expenseRows = expenses.map(expense => [
        expense.expense_date,
        expense.title,
        expense.category?.name || '',
        expense.amount.toString(),
        expense.payment_method || '',
        expense.status
      ]);
      return [expenseHeaders, ...expenseRows].map(row => row.join(',')).join('\n');

    case 'profit_loss':
      const pl = data as ProfitLossReport;
      return `Profit & Loss Report
Period: ${pl.period.start_date} to ${pl.period.end_date}

REVENUE
Total Revenue,${pl.revenue.total_revenue}
Dine-in Revenue,${pl.revenue.dine_in_revenue}
Pickup Revenue,${pl.revenue.pickup_revenue}
Delivery Revenue,${pl.revenue.delivery_revenue}
Net Revenue,${pl.revenue.net_revenue}

COST OF GOODS SOLD
Total COGS,${pl.cost_of_goods_sold.total_cogs}
COGS Percentage,${pl.cost_of_goods_sold.cogs_percentage}%

GROSS PROFIT
Amount,${pl.gross_profit.amount}
Margin,${pl.gross_profit.margin_percentage}%

OPERATING EXPENSES
Operating Expenses,${pl.operating_expenses.operating_expenses}
Administrative Expenses,${pl.operating_expenses.administrative_expenses}
Other Expenses,${pl.operating_expenses.other_expenses}
Total Operating Expenses,${pl.operating_expenses.total_operating_expenses}

NET PROFIT
Amount,${pl.net_profit.amount}
Margin,${pl.net_profit.margin_percentage}%`;

    default:
      return JSON.stringify(data, null, 2);
  }
}
